import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { EmojiCategory as EmojiCategoryType, SkinTone, SkinToneOption } from '@appTypes/emoji';
import { EmojiSubgroup } from './EmojiSubgroup';
import { EmojiCard } from './EmojiCard';

interface EmojiCategoryProps {
  category: EmojiCategoryType;
  onEmojiClick: (emoji: string, event: React.SyntheticEvent<HTMLElement>) => void;
  skinTone?: SkinTone;
  skinToneOptions?: SkinToneOption[];
}

const LazySubgroup = React.memo(
  ({ children, emojiCount }: { children: React.ReactNode; emojiCount: number }) => {
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const el = ref.current;
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        },
        { rootMargin: '300px' }
      );
      observer.observe(el);
      return () => observer.disconnect();
    }, []);

    const estimatedHeight = 40 + Math.ceil(emojiCount / 10) * 70;

    return (
      <div ref={ref} style={!isVisible ? { minHeight: estimatedHeight } : undefined}>
        {isVisible ? children : null}
      </div>
    );
  }
);
LazySubgroup.displayName = 'LazySubgroup';

export const EmojiCategory = React.memo(
  ({ category, onEmojiClick, skinTone = 'default', skinToneOptions }: EmojiCategoryProps) => {
    const { t } = useTranslation();

    // Check if category has subgroups or direct emojis
    const hasSubgroups = category.subgroups && category.subgroups.length > 0;
    const hasEmojis = category.emojis && category.emojis.length > 0;

    if (!hasSubgroups && !hasEmojis) {
      return null;
    }

    // Get the category name using the translation key
    const getLocalizedCategoryName = () => {
      return t(category.nameKey, category.id || 'Category');
    };

    return (
      <Box className="emoji-category" sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {getLocalizedCategoryName()}
        </Typography>

        {hasSubgroups ? (
          // Render subgroups with lazy loading
          category.subgroups!.map((subgroup) => (
            <LazySubgroup key={subgroup.id} emojiCount={subgroup.emojis.length}>
              <EmojiSubgroup
                subgroup={subgroup}
                onEmojiClick={onEmojiClick}
                skinTone={skinTone}
                skinToneOptions={skinToneOptions}
              />
            </LazySubgroup>
          ))
        ) : (
          // Fallback for categories with direct emojis (backward compatibility)
          <LazySubgroup emojiCount={category.emojis!.length}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(64px, 1fr))',
                gap: 0.75,
              }}
            >
              {category.emojis!.map((emoji, index) => (
                <EmojiCard
                  key={`${emoji.char}-${index}`}
                  emoji={emoji}
                  onEmojiClick={onEmojiClick}
                  skinTone={skinTone}
                  skinToneOptions={skinToneOptions}
                />
              ))}
            </Box>
          </LazySubgroup>
        )}
      </Box>
    );
  }
);
EmojiCategory.displayName = 'EmojiCategory';
